/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Tue Oct 24 2023
 **/

import Slider from "react-slick";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { ChronoUtils } from "@/frameworks/libraries/utilities";
import { _T, _P } from "@rfice/language";
import { Button, Column, Icon, IconName, Row, Typography } from "@/frameworks/designStuff/components";
import { useAccount, useDevice } from "@/frameworks/core/API";
import { useAccountActions, useConfirm } from "@/frameworks/core/hooks";

import * as S from "../../emotion";
import Layout from "../../Layout";

interface DeviceInterface {
	title: string;
	active: boolean;
	deviceType: "Phone" | " Desktop" | "IOS" | "WEB" | "ANDROID" | "Other";
	country?: string;
	city?: string;
	deviceKey: string;
	lastActiveTime: number;
}

function Device({ deviceType, active, title, country, city, deviceKey, lastActiveTime }: DeviceInterface) {
	const { confirm } = useConfirm();
	const accountAPI = useAccount();

	let device = "";
	switch (deviceType) {
		case "Phone":
			device = "Phone";
			break;
		case "ANDROID":
			device = "Phone";
			break;
		case "IOS":
			device = "Phone";
			break;
		case "WEB":
			device = "Desktop";
			break;
		default:
			device = "Etc";
	}

	const handleLogoutDevice = async () => {
		confirm(
			{
				descriptions: _T("해당 기기에서 로그아웃하시겠습니까?")
			},
			{
				async onConfirm() {
					try {
						const deviceKeys = [deviceKey];
						await accountAPI.logoutOthers.execute({ deviceKeys });
					} catch (error) {
						console.error("Error logging out this device:", error);
					}
				}
			}
		);
	};

	return (
		<Row horizonAlign="center">
			<Column horizonAlign="center">
				<Row css={{ position: "relative", height: 44, width: 44 }}>
					<Icon name={`${device}${active ? "On" : "Off"}Unique24` as IconName} size={44} />
					<Button.Icon
						css={{
							position: "absolute",
							right: -17,
							top: 0,
							width: 20,
							height: 20
						}}
						type="button"
						name="Close24"
						size="small_18"
						filled="error"
						fill="neutral_00_100"
						onClick={handleLogoutDevice}
					/>
				</Row>
				<Typography typoSize="Button2" typoColor="neutral_70_100" style={{ paddingTop: "4px" }}>
					{title}
				</Typography>
				<Typography typoSize="Body3" typoColor="neutral_60_100">
					{country}/{city}
				</Typography>
				<Typography typoSize="Body3" typoColor="neutral_60_100">
					{active ? _T("접속중") : ChronoUtils.formatTimeAgo(lastActiveTime)}
				</Typography>
			</Column>
		</Row>
	);
}

export default function LoginInfo() {
	const accountAPI = useAccount();
	const deviceAPI = useDevice();
	const { logout } = useAccountActions();

	const [info, setInfo] = useState<WebServices.Device.List.Data>();
	const [loginInfo, setLoginInfo] = useState<WebServices.Log.Data>();
	const dataLength = info ? info.length : 0;

	let slidesToShow = Math.min(5, dataLength);
	slidesToShow = Math.max(1, slidesToShow);

	const settings = {
		dots: true,
		slidesToShow: Number(slidesToShow),
		slidesToScroll: Number(slidesToShow),
		initialSlide: 0
	};

	useEffect(() => {
		accountAPI.loginLog.execute().then(({ data }) => {
			setLoginInfo(data);
		});
		deviceAPI.list.execute().then(({ data }) => {
			setInfo(data);
		});
	}, []);

	const handleLogoutOther = async () => {
		try {
			const response = await deviceAPI.list.execute();
			const deviceKeys = response.data?.map((device: WebServices.Device.List.Item) => device.deviceKey);
			await accountAPI.logoutOthers.execute({ deviceKeys });
			logout();
		} catch (error) {
			console.error("Error logging out other devices:", error);
		}
	};

	const sortRecentLoginInfo = loginInfo?.content.sort((a, b) => b.createdTime - a.createdTime);
	const recentLoginInfo = sortRecentLoginInfo?.slice(0, 15);

	return (
		<Layout title="로그인 정보">
			<Column gap={24} style={{ width: "100%" }}>
				<Row horizonAlign="distribute" style={{ width: "100%" }}>
					<Row style={{ whiteSpace: "pre-line", textAlign: "start" }}>
						<Typography>
							{_P(
								"해당 기기의 최신 로그인 이력이 표시됩니다.<br />삭제하는 경우 해당 기기의 로그인 상태가 로그아웃 됩니다.<br />오래된 디바이스는 표시되지 않을 수 있습니다."
							)}
						</Typography>
					</Row>
					<Button filled="transparent" onClick={handleLogoutOther}>
						<Typography typoSize="Button2_underline" typoColor="accent_50_100">
							{_T("모든 세션에서 로그아웃")}
						</Typography>
					</Button>
				</Row>
				<div style={{ width: "536px", justifyContent: "space-between" }}>
					<Slider {...settings} dotsClass="slick-dot" arrows={false}>
						{info?.map((log, index) => (
							<Device
								key={`${index}-table-body`}
								country={log.countryCode}
								city={log.city}
								deviceType={log.type as DeviceInterface["deviceType"]}
								title={log.type}
								active={log.active}
								deviceKey={log.deviceKey}
								lastActiveTime={log.lastActiveTime || 0}
							/>
						))}
					</Slider>
				</div>
				<Typography typoSize="Title1" typoColor="neutral_80_100">
					{_T("로그인 이력 (최근 90일)")}
				</Typography>
				<Row style={{ width: "100%" }}>
					<table width="100%">
						<S.THead>
							<tr key="table-header">
								<S.Th>{_T("로그인 일시")}</S.Th>
								<S.Th>{"IP"}</S.Th>
								<S.Th>{_T("디바이스")}</S.Th>
								<S.Th>{_T("접속위치")}</S.Th>
							</tr>
						</S.THead>
						<tbody>
							{recentLoginInfo?.map((log, index) => (
								<tr key={`${index}-table-body`}>
									<S.Td>{dayjs(log.createdTime).format("YYYY.MM.DD HH:mm:ss")}</S.Td>
									<S.Td>{log.ip}</S.Td>
									<S.Td>{log.deviceType}</S.Td>
									<S.Td>
										{log.country?.toUpperCase()}/{log.city}
									</S.Td>
								</tr>
							))}
						</tbody>
					</table>
				</Row>
			</Column>
		</Layout>
	);
}
